"use client";

import { FC } from "react";

import highlightTextWithHTML from "apps/website/utils/website/highlightTextWithHTML";
import {
  boldenBacktickText,
} from "apps/website/utils/website/boldenBacktickText";
import useSectionScrollPosition from "apps/website/hooks/useSectionScrollPosition";

import Section from "../../layout/Section/Section";
import Container from "../../layout/Container/Container";
import Image, { IImageProps } from "../../base/Image/Image";
import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";
import Icon from "../../base/Icon/Icon";
import TextBody from "../../base/Text/TextBody/TextBody";
import Button from "../../base/Button/Button";
import { StickyCTA } from "../../feature/StickyCTA/StickyCTA";
import Text from "../../base/Text/Text";
import { legacySizeCollectionMap } from "../../base/Text/Text.map";
import TextTitle from "../../base/Text/TextTitle/TextTitle";

export interface ITrialItem {
  id: string;
  label: string;
}

export interface ISectionTrialOffer {
  title: string;
  boxTitle: string;
  boxSubtitle: string;
  trialItems: ITrialItem[];
  totalPrice: string;
  pricePerMeal: string;
  image: IImageProps;
  ctaText: string;
  ctaHref: string;
  footerText?: string;
  showStickyCTA?: boolean;
  stickyCTAText?: string;
  id?: string;
  centerTrialItems?: boolean;
}

const SectionTrialOffer: FC<ISectionTrialOffer> = ({
  title,
  boxTitle,
  boxSubtitle,
  trialItems,
  totalPrice,
  pricePerMeal,
  image,
  ctaText,
  ctaHref,
  footerText = "No lock-in: Pause or cancel anytime",
  showStickyCTA = false,
  stickyCTAText,
  id,
  centerTrialItems = false,
}) => {
  const [ sectionRef, showSticky ] = useSectionScrollPosition<HTMLDivElement>();

  const formattedSubtitle = highlightTextWithHTML({
    text: `\`${boxSubtitle}\``,
    theme: "brand",
    display: "inline",
    rotate: false,
  });

  return (
    <>
      <Section
        component={ SectionTrialOffer.name }
        theme="light-grey"
        size="none"
        id={ id }
      >
        <Container>
          <div ref={ sectionRef } className="flex flex-col">
            { /* Title */ }
            <div className="mb-4 md:mb-8 relative px-2">
              <div className="absolute top-0 left-0 md:hidden">
                <Icon icon="burstTopLeft" size="xsmall" color="default" />
              </div>
              <div className="absolute bottom-0 right-0 md:hidden">
                <Icon icon="burstBottomRight" size="xsmall" color="default" />
              </div>
              <Text
                tag={ "h2" }
                display="title"
                size={ legacySizeCollectionMap.titleLg }
                align="center"
                className="hidden md:block"
              >
                { title }
              </Text>
              <TextTitle
                align="center"
                data-testid="SectionTrialOfferTitle"
                size={ 2 }
                className="block md:hidden"
              >
                { title }
              </TextTitle>
            </div>

            <div className="w-full h-full flex flex-col justify-center items-center md:flex-row md:gap-16">
              { /* Image */ }
              <div className="w-full lg:flex-1">
                <Image
                  image={ image }
                  alt={ image.alt ?? "Trial offer" }
                  data-testid="SectionTrialOfferImage"
                />
              </div>

              { /* Table */ }
              <div className="w-full lg:flex-1">
                <div className="flex flex-col justify-center p-4 gap-y-4 bg-white">
                  <div className="space-y-1">
                    <TextSubtitle size={ 1 }>{ boxTitle }</TextSubtitle>
                    <TextSubtitle size={ 2 }>
                      <span
                        dangerouslySetInnerHTML={ { __html: formattedSubtitle } }
                      />
                    </TextSubtitle>
                  </div>

                  <div className="flex flex-col gap-3">
                    { trialItems.map((item) => (
                      <div
                        key={ item.id }
                        className={ `flex gap-2 ${centerTrialItems ? "items-center" : "items-start"}` }
                        data-testid={ `info-item-${item.id}` }
                      >
                        <div>
                          <Icon icon="tickInverted" size="small" color="success" />
                        </div>
                        <TextBody size={ 2 } className="!mb-0">
                          { boldenBacktickText(item.label) }
                        </TextBody>
                      </div>
                    )) }
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between bg-light-grey-75 p-2">
                      <TextSubtitle size={ 2 }>Total today</TextSubtitle>
                      <TextSubtitle size={ 2 }>{ totalPrice }</TextSubtitle>
                    </div>

                    <div className="flex items-center justify-between px-2 py-1">
                      <TextSubtitle size={ 4 }>Price per meal</TextSubtitle>
                      <TextSubtitle size={ 4 }>{ pricePerMeal }</TextSubtitle>
                    </div>
                  </div>
                </div>

                { /* CTA */ }
                <div className="flex flex-col items-center gap-2 mt-4 md:mt-5">
                  <Button to={ ctaHref } data-testid="claim-trial-button">
                    { ctaText }
                  </Button>

                  { footerText && (
                    <div className="flex items-center justify-center gap-1">
                      <Icon icon="tickInverted" size="small" color="default" />
                      <TextBody size={ 3 } className="!mb-0">
                        { footerText }
                      </TextBody>
                    </div>
                  ) }
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      { showStickyCTA && showSticky && (
        <StickyCTA
          position="fixed"
          theme="brand"
          button={ {
            text: stickyCTAText ?? ctaText,
            href: ctaHref,
            size: "full-width",
            color: "secondary",
          } }
        />
      ) }
    </>
  );
};

export default SectionTrialOffer;
